import { AxiosInstance } from 'axios';

export interface AchievementOption {
  idOsiagniecia: number;
  nazwa: string;
}

// Pobiera lata i ustawia stan
export const getYears = async (
  axiosInstance: AxiosInstance,
  setYears: (years: string[]) => void
): Promise<void> => {
  try {
    const response = await axiosInstance.get('/bands/years');
    const yearsAsStrings = response.data.map((year: string | null) => year || 'wszystkie');
    setYears(['wszystkie', ...yearsAsStrings]);
  } catch (error) {
    console.error('Błąd podczas pobierania lat:', error);
  }
};

// Pobiera statusy i ustawia stan
export const getStatuses = async (
  axiosInstance: AxiosInstance,
  setStatuses: (statuses: string[]) => void
): Promise<void> => {
  try {
    const response = await axiosInstance.get('/bands/statuses');
    const statusesWithDefaults = response.data.map((status: string | null) => status || '');
    setStatuses(['wszystkie', ...statusesWithDefaults]);
  } catch (error) {
    console.error('Błąd podczas pobierania statusów:', error);
  }
};

// Pobiera zespoły z filtrami i ustawia stan
export const getBandsWithFilters = async (
  axiosInstance: AxiosInstance,
  year: string,
  status: string,
  setItems: (items: string[]) => void,
  achievementId: string = 'wszystkie'
): Promise<void> => {
  try {
    const query = new URLSearchParams({
      year: year === 'wszystkie' ? '' : year,
      status: status === 'wszystkie' ? '' : status,
      achievementId: achievementId === 'wszystkie' ? '' : achievementId,
    });
    const response = await axiosInstance.get(`/bands/filter?${query.toString()}`);
    setItems(response.data);
  } catch (error) {
    console.error('Błąd podczas filtrowania zespołów:', error);
    setItems([]);
  }
};

export const getAchievements = async (
  axiosInstance: AxiosInstance,
  setAchievements: (achievements: AchievementOption[]) => void
): Promise<void> => {
  try {
    const response = await axiosInstance.get<AchievementOption[]>('/achievements');
    setAchievements(response.data);
  } catch (error) {
    console.error('Blad podczas pobierania osiagniec:', error);
    setAchievements([]);
  }
};

// Pobiera wszystkie zespoły i ustawia stan
export const getBands = async (
  axiosInstance: AxiosInstance,
  setItems: (items: string[]) => void,
): Promise<void> => {
  try {
    const response = await axiosInstance.get('/bands/all');
    const bandNames = response.data
      .filter((band: { nazwa: string | null }) => band.nazwa !== null)
      .map((band: { nazwa: string }) => band.nazwa);
    setItems(bandNames);
  } catch (error) {
    console.error('Błąd podczas pobierania zespołów:', error);
    setItems([]);
  }
};

export const setStars = async (
  axiosInstance: AxiosInstance,
  bandName: string, // Use band name instead of band ID
  isStar: 0 | 1,
  onSuccess: (response: any) => void,
  onError: (error: any) => void
): Promise<void> => {
  try {
    const response = await axiosInstance.patch(`/bands/${bandName}`, { isStar });
    onSuccess(response.data);
  } catch (error) {
    console.error("Błąd podczas aktualizowania statusu gwiazdy:", error);
    onError(error);
  }
};

// Pobiera wydarzenia i ustawia stan
export const getEventsType = async (
  axiosInstance: AxiosInstance,
  setItems: (items: string[]) => void
): Promise<void> => {
  try {
    const response = await axiosInstance.get<{ nazwa: string | null }[]>('/eventsType/names');
    const eventNames = response.data
      .map((event) => event.nazwa ?? '')
      .filter((nazwa) => nazwa.trim() !== '');
    setItems(eventNames);
  } catch (error) {
    console.error('Błąd podczas pobierania wydarzeń:', error);
    setItems([]);
  }
};

// Pobiera gwiazdy i ustawia stan
export const getStarBands = async (
  axiosInstance: AxiosInstance,
  setItems: (items: string[]) => void,
): Promise<void> => {
  try {
    const response = await axiosInstance.get('/bands/stars');
    const starBands = response.data.map((band: { nazwa: string }) => band.nazwa);
    setItems(starBands);
  } catch (error) {
    console.error('Błąd podczas pobierania gwiazd:', error);
    setItems([]);
  }
};

export const saveEventType = async (
  axiosInstance: AxiosInstance,
  event: { title: string; color: string , time: number},
  onSuccess: (response: any) => void,
  onError: (error: any) => void
): Promise<void> => {
  try {
    const response = await axiosInstance.post('/eventsType', {
      title: event.title,
      color: event.color,
      time: event.time
    });
    onSuccess(response.data);
  } catch (error) {
    console.error('Błąd podczas zapisywania wydarzenia:', error);
    onError(error);
  }
};

export const getTotalSongDuration = async (
  axiosInstance: AxiosInstance,
  teamName: string,
  setDuration: (duration: number) => void
): Promise<void> => {
  try {
    const response = await axiosInstance.get(`/bands/songs-duration`, {
      params: { teamName },
    });
    console.log(response.data.totalDuration);
    setDuration(response.data.totalDuration || 0);
  } catch (error) {
    console.error('Błąd podczas pobierania długości piosenek:', error);
    setDuration(0);
  }
};

export const addPerformance = async (
  axiosInstance: AxiosInstance,
  name: string,
  duration: number,
  isStar: boolean,
  concertId: number,
  concertVersion: number,
  insertBeforeConcertOrder: number | null,
  withSetupEvent: boolean,
  onSuccess: (response: any) => void,
  onError: (error: any) => void
): Promise<void> => {
  try {
    const response = await axiosInstance.post('/events/add-performance', {
      name,
      duration,
      isStar,
      concertId,
      wersja: concertVersion,
      insertBeforeConcertOrder,
      withSetupEvent,
    });
    onSuccess(response.data);
  } catch (error) {
    console.error('Błąd podczas dodawania występu:', error);
    onError(error);
  }
};

export const addEvent = async (
  axiosInstance: AxiosInstance,
  nazwa: string,
  typWydarzenia: string,
  czasTrwania: number,
  idKoncertu: number,
  concertVersion: number,
  onSuccess: (response: any) => void,
  onError: (error: any) => void
): Promise<void> => {
  try {
    const response = await axiosInstance.post('/events/add-event', {
      nazwa,
      typWydarzenia,
      czasTrwania,
      idKoncertu,
      wersja: concertVersion,
    });
    onSuccess(response.data);
  } catch (error) {
    console.error('Błąd podczas dodawania wydarzenia:', error);
    onError(error);
  }
};

export const getEventDuration = async (
  axiosInstance: AxiosInstance,
  eventName: string,
  setDuration: (duration: number) => void
): Promise<void> => {
  try {
    const response = await axiosInstance.get(`/events/duration`, {
      params: { eventName },
    });
    setDuration(response.data.duration || 100);
  } catch (error) {
    console.error('Błąd podczas pobierania czasu trwania wydarzenia:', error);
    setDuration(0);
  }
};

export const getStarPerformanceDuration = async (
  axiosInstance: AxiosInstance,
  setDuration: (duration: number) => void
): Promise<void> => {
  try {
    const response = await axiosInstance.get('/bands/stars-duration');
    setDuration(response.data.duration || 20);
  } catch (error) {
    console.error('Błąd podczas pobierania czasu trwania występu gwiazdy:', error);
    setDuration(20); // Domyślnie 20 minut
  }
};

export const fetchConcertVersion = async (
  axiosInstance: AxiosInstance,
  concertId: number
): Promise<number> => {
  try {
    const response = await axiosInstance.get(`/concerts/version/${concertId}`);
    return response.data.version;
  } catch (error) {
    console.error("Błąd podczas pobierania wersji koncertu:", error);
    throw error;
  }
};
